import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartTheme } from '../../lib/chartColors';

export interface ChartSeries {
  key: string;
  name: string;
  color: string;
}

interface SeriesChartProps {
  data: ReadonlyArray<object>;
  xKey: string;
  series: ChartSeries[];
  type?: 'bar' | 'area';
  stacked?: boolean;
  height?: number;
  /** Format the x-axis tick / tooltip label (e.g. shorten ISO timestamps). */
  xFormatter?: (value: string) => string;
  valueFormatter?: (value: number, key: string) => string;
  emptyMessage?: string;
  loading?: boolean;
  showLegend?: boolean;
}

/** Themed recharts bar/area chart for time series. Colours come from `lib/chartColors`. */
export default function SeriesChart({
  data,
  xKey,
  series,
  type = 'bar',
  stacked = true,
  height = 260,
  xFormatter,
  valueFormatter,
  emptyMessage = 'No data for this period.',
  loading,
  showLegend = true,
}: SeriesChartProps) {
  const hasData =
    data.length > 0 &&
    data.some((row) => series.some((s) => Number((row as Record<string, unknown>)[s.key] ?? 0) > 0));

  if (loading) {
    return <div className="animate-pulse rounded-xl bg-glass-3/50" style={{ height }} aria-hidden />;
  }
  if (!hasData) {
    return (
      <div className="flex items-center justify-center text-sm text-glass-text-3" style={{ height }}>
        {emptyMessage}
      </div>
    );
  }

  const stackId = stacked ? 'stack' : undefined;
  const tooltipProps = {
    contentStyle: chartTheme.tooltip,
    labelStyle: { color: chartTheme.tooltip.color, fontWeight: 600 },
    labelFormatter: (label: unknown) => (xFormatter ? xFormatter(String(label)) : String(label)),
    formatter: (value: unknown, name: unknown, item: { dataKey?: unknown }) => {
      const num = typeof value === 'number' ? value : Number(value);
      const key = typeof item?.dataKey === 'string' ? item.dataKey : String(name);
      return [valueFormatter ? valueFormatter(num, key) : num.toLocaleString(), String(name)] as [string, string];
    },
  };
  const axisProps = {
    tick: chartTheme.tick,
    stroke: chartTheme.axisStroke,
  };
  const mutableData = data as Array<object>;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {type === 'area' ? (
          <AreaChart data={mutableData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
            <XAxis dataKey={xKey} tickFormatter={xFormatter} {...axisProps} minTickGap={24} />
            <YAxis {...axisProps} allowDecimals={false} width={40} />
            <Tooltip {...tooltipProps} />
            {showLegend && <Legend wrapperStyle={chartTheme.legend} />}
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stackId={stackId}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.25}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart data={mutableData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
            <XAxis dataKey={xKey} tickFormatter={xFormatter} {...axisProps} minTickGap={24} />
            <YAxis {...axisProps} allowDecimals={false} width={40} />
            <Tooltip {...tooltipProps} cursor={{ fill: 'rgb(var(--glass-border) / 0.15)' }} />
            {showLegend && <Legend wrapperStyle={chartTheme.legend} />}
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId={stackId}
                fill={s.color}
                radius={!stacked || i === series.length - 1 ? [4, 4, 0, 0] : undefined}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
